import { decodeHtmlEntities } from "../../../../../common/helpers/decodeHtmlEntities";
import type { RawTaxonomyTranslation } from "../../../../../types";

// Define a generic constraint for objects that have translatable properties.
// This ensures any object passed to our function has the necessary structure.
interface TranslatableTerm {
  name: string;
  description: string;
  translations?:
    | { [languageCode: string]: RawTaxonomyTranslation }
    | Record<string, never>;
}

// Define the language dependency type
interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * A pure, generic helper function that transforms a taxonomy term (like a category or tag)
 * by applying translations if they are available for the current language.
 *
 * @param term - The raw term object (e.g., RawWPRestProductCategory or RawWPRestProductTag).
 * @param languageInfo - An object containing the current language and translation status.
 * @returns A new, transformed term object if translations were applied, otherwise the original term.
 */
export const transformTranslatableTerm = <T extends TranslatableTerm>(
  term: T,
  languageInfo: LanguageInfo
): T => {
  const { currentLanguage, shouldTranslate } = languageInfo;

  let finalName = term.name;
  let finalDescription = term.description;

  // Check if translation is necessary and possible
  if (
    shouldTranslate &&
    term.translations &&
    typeof term.translations === "object" &&
    term.translations[currentLanguage]
  ) {
    const translation = term.translations[currentLanguage];

    // Conditionally override name if a non-empty translation exists
    if (translation.name && translation.name.trim() !== "") {
      finalName = translation.name;
    }

    // Conditionally override description
    if (translation.description && translation.description.trim() !== "") {
      finalDescription = translation.description;
    }
  }

  // Always return a new object with the decoded name
  return {
    ...term,
    name: decodeHtmlEntities(finalName),
    description: finalDescription,
  };
};
